import type { KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  FieldComponent,
  type FormField,
  useFormDispatch,
  useFormSelector
} from '@elfsight/widget-form-builder/widget';
import { type TextControlBaseInstance } from '@elfsight/widget-ui';
import { EmailDisplayElement } from '../blocks';
import { useCheckoutColors } from '../providers';
import { useCheckoutFormFieldProps } from '../utils';

export type PaymentConfirmationEmailProps = {
  onEmailChange: (email: string) => void;
  initialValue: string;
  emailField: FormField;
};

type PaymentConfirmationEmailMode = 'display' | 'edit';

export function PaymentConfirmationEmail({
  onEmailChange,
  initialValue,
  emailField
}: PaymentConfirmationEmailProps) {
  const fieldRef = useRef<TextControlBaseInstance>(null);
  const { textColor, accentColor } = useCheckoutColors();

  const [value, setValue] = useState(initialValue);
  const [mode, setMode] = useState<PaymentConfirmationEmailMode>(
    initialValue ? 'display' : 'edit'
  );

  const { fields, fieldsError } = useFormSelector();
  const dispatch = useFormDispatch();

  const fieldProps = useCheckoutFormFieldProps();

  const focusEmailInput = () => {
    requestAnimationFrame(() => {
      fieldRef.current?.focusInput();
    });
  };

  const toEditMode = () => {
    setMode('edit');
    focusEmailInput();
  };

  const toDisplayMode = () => {
    setMode('display');
  };

  const handleInputBlur = () => {
    const emailValue = fields[emailField.id]?.value;
    const isEmpty = typeof emailValue !== 'string' || !emailValue;

    if (fieldsError.invalid || isEmpty) {
      dispatch({
        type: 'changeField',
        field: { id: emailField.id, valid: false }
      });

      if (isEmpty) {
        setValue('');
        onEmailChange('');
      }

      return;
    }

    if (value !== emailValue) {
      onEmailChange(emailValue);
    }

    setValue(emailValue);
    toDisplayMode();
  };

  const blurInputOnEnter = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    }
  };

  useEffect(() => {
    if (fields[emailField.id] === undefined) {
      dispatch({
        type: 'changeField',
        field: { id: emailField.id, value: initialValue, valid: true }
      });
    }
  }, [fields, emailField.id, initialValue, dispatch]);

  useEffect(() => {
    focusEmailInput();
  }, []);

  if (mode === 'display') {
    return (
      <EmailDisplayElement
        textColor={textColor}
        accentColor={accentColor}
        email={value}
        toEditMode={toEditMode}
      />
    );
  }

  if (mode === 'edit') {
    return (
      <KeyboardBlurWrapper onKeyDown={blurInputOnEnter}>
        <Field
          {...fieldProps}
          ref={fieldRef}
          field={emailField}
          onBlur={handleInputBlur}
        />
      </KeyboardBlurWrapper>
    );
  }

  return null;
}

const KeyboardBlurWrapper = styled.div`
  width: 100%;
`;

const Field = styled(FieldComponent)`
  width: 100%;
  margin-bottom: 0 !important;
`;
